import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import TextBoxViewModelT from '@/ViewModels/Controls/TextBoxViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import TextBoxT from '@/Controls/TextBox';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';
import TypeConverter from '@/Utility/TypeConverter';
import NumericTextBoxViewModelT from '@/ViewModels/Controls/NumericTextBoxViewModel';
import NumericTextBoxT from '@/Controls/NumericTextBox';
import Utilities from '@/Utility/Utilities';

export default class TextBoxFactory extends ControlFactory {
    /**
     * @override
     */
    public viewModel: TextBoxViewModelT;

    private controlHtmlElement: JQuery;
    private useNumericTextBoxViewModel: boolean = false;

    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelT>('TextBoxViewModel');
    private TextBox = ComponentManager.getControl<typeof TextBoxT>('TextBox');
    private NumericTextBoxViewModel = ComponentManager.getViewModel<typeof NumericTextBoxViewModelT>('NumericTextBoxViewModel');
    private NumericTextBox = ComponentManager.getControl<typeof NumericTextBoxT>('NumericTextBox');

    public constructor(private params: any) {
        super(params);
    }

    /**
     * @override
     */
    control: TextBoxT;

    /**
     * @override
     */
    public getControl(): JQuery {
        let info: NumericTypeHelper.DataInfo = null;
        let floatingPrecision: number = null;

        // Wenn es ein Array ist, muss die TextBox verwendet werden
        if (this.detailViewItem.dotNetTypeName != null && !TypeConverter.typeIsArray(this.detailViewItem.dotNetTypeName)) {
            const isIntegerOrLongOrShort: boolean = TypeConverter.typeIsInt(this.detailViewItem.dotNetTypeName);
            const isByte: boolean = TypeConverter.typeIsByte(this.detailViewItem.dotNetTypeName);
            const isFloat: boolean = TypeConverter.typeIsSingle(this.detailViewItem.dotNetTypeName);
            const isDecimal: boolean = TypeConverter.typeIsDecimal(this.detailViewItem.dotNetTypeName);
            const isDouble: boolean = TypeConverter.typeIsDouble(this.detailViewItem.dotNetTypeName);
            info = NumericTypeHelper.getDotNetTypeInfo(this.detailViewItem.dotNetTypeName);

            // Wenn es eine rationale Zahl ist, wird in dem NumericTextBoxViewModel die Praezision von der aktuellen Sprache ausgewaehlt
            floatingPrecision = isIntegerOrLongOrShort || isByte ? 0 : null;
            this.useNumericTextBoxViewModel = isFloat || isDecimal || isDouble || isIntegerOrLongOrShort || isByte;
        }

        if (this.controlHtmlElement != null) {
            return this.controlHtmlElement;
        }

        let ViewModel = null;
        let Control = null;
        let params = null;

        if (this.useNumericTextBoxViewModel) {
            ViewModel = this.NumericTextBoxViewModel;
            Control = this.NumericTextBox;
            params = this.createParamForNumericViewModel(info, floatingPrecision);
        } else {
            ViewModel = this.TextBoxViewModel;
            Control = this.TextBox;
            params = this.createParamForViewModel();
        }

        this.viewModel = new ViewModel(params);
        this.control = new Control(this.viewModel);

        const tuple = this.control.getTuple();
        this.controlHtmlElement = tuple.control;

        const innerControl = this.controlHtmlElement.find('.c-textbox');
        if (innerControl?.length > 0)
            this.addOverwritableToControl(innerControl, [this.detailViewItem.name]);
        else
            this.addOverwritableToControl(this.controlHtmlElement, [this.detailViewItem.name]);
        
        this.registerDefaultValidation([this.detailViewItem.name]);
        this.registerTypeValidation(this.detailViewItem.name, this.textBoxTypeValidation.bind(this));

        if (!this.useNumericTextBoxViewModel) {
            this.registerCustomValidation([this.detailViewItem.name], this.viewModel.value, (value: string): boolean => {
                return Utilities.validateInputForInvalidCharacters(value);
            });
        }

        return this.controlHtmlElement;
    }

    private textBoxTypeValidation(value: string, dotNetTypeName: string): boolean {
        if (dotNetTypeName != null) {
            const isIntegerOrLongOrShort: boolean = TypeConverter.typeIsInt(dotNetTypeName);
            const isString: boolean = TypeConverter.typeIsString(dotNetTypeName) || TypeConverter.typeIsEncryptedString(dotNetTypeName);
            const isFloat: boolean = TypeConverter.typeIsSingle(dotNetTypeName);
            const isList: boolean = TypeConverter.typeIsArray(dotNetTypeName);
            const isNullable: boolean = TypeConverter.typeIsNullable(dotNetTypeName);
            const isChar: boolean = TypeConverter.typeIsChar(dotNetTypeName);
            const isByte: boolean = TypeConverter.typeIsByte(dotNetTypeName);
            const isDecimal: boolean = TypeConverter.typeIsDecimal(dotNetTypeName);
            const isDouble: boolean = TypeConverter.typeIsDouble(dotNetTypeName);
            const info = NumericTypeHelper.getDotNetTypeInfo(dotNetTypeName);

            if (this.detailViewItem.useInFilter && String.isNullOrEmpty(value)) {
                return true;
            }

            // Fuer not nullable Listen
            if (isList && !isNullable && value.length === 1 && value[0] === '') {
                return false;
            }

            if (isNullable && String.isNullOrEmpty(value)) {
                return true;
            }

            if (!isString && !isNullable && String.isNullOrEmpty(value)) {
                return false;
            }

            const numericRangeCheck = (value) => {
                if (info == null)
                    return undefined;

                return !isNaN(value) && value >= info.min && value <= info.max;
            };

            let parsingFunc = null;
            let paramFunc = null;

            if (isIntegerOrLongOrShort || isByte) {
                paramFunc = numericRangeCheck;
                parsingFunc = TextBoxFactory.isNaturalNumber;
            } else if (isFloat || isDecimal || isDouble) {
                paramFunc = numericRangeCheck;
                parsingFunc = TextBoxFactory.isWholeNumber;
            } else if (isChar) {
                parsingFunc = TextBoxFactory.parseChar;
            }

            if (isList) {
                return TextBoxFactory.parseArrayOfType((value as any), (valueI) => {
                    if (parsingFunc != null) {
                        return parsingFunc(valueI, paramFunc);
                    } else if (info != null) {
                        return numericRangeCheck(value);
                    }
                });
            }

            if (parsingFunc != null) {
                return parsingFunc(value, paramFunc);
            } else if (info != null) {
                return numericRangeCheck(value);
            }
        }
        return true;
    }

    // @Override
    public getValueForFilter() {
        let ret = {};
        ret[this.detailViewItem.name] = this.context.genericObject.getValue(this.detailViewItem.name);
        return ret;
    }

    // @Override
    public getFilterInfo(): FilterInfo {
        return {
            field: this.detailViewItem.name,
            value: this.viewModel?.value() || ''
        }
    }

    private getLength(): number {
        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            if (this.detailViewItem.dotNetTypeName == null || !TypeConverter.typeIsDouble(this.detailViewItem.dotNetTypeName)) {
                length = 255;
            }
            else {
                length = 326;
            }
        }
        return length;
    }

    // ToDo: Redundanter Code in der NumericTextBoxFactory. Nach Portierung nach Typescript muss dies nochmal bearbeitet werden.
    private createParamForNumericViewModel(dotNetTypeInfo: NumericTypeHelper.DataInfo, floatingPrecision: number) {
        const parameter = {
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isFocusable: this.isFocusable,
            invalidationText: this.invalidationText,
            maxLength: this.getLength(),
            min: dotNetTypeInfo.min,
            max: dotNetTypeInfo.max,
            floatingPrecision: floatingPrecision,
            value: this.context.genericObject.getObservable(this.detailViewItem.name),
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            technicalName: this.detailViewItem.name,
            showSpinners: false,
            label: new LabelViewModelT({
                isVisible: !!this.detailViewItem.unitErgName,
                ergName: this.detailViewItem.unitErgName
            }),
            tooltip: new LabelViewModelT({
                ergName: this.detailViewItem.tooltip
            }),
            onSubmit: this.params.onSubmit,
            useUnlimitedDecimals: this.detailViewItem.useInFilter
        }

        return parameter;
    }

    public createParamForViewModel(): any {
        const parameter = {
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            invalidationText: this.invalidationText,
            isFocusable: this.isFocusable,
            isSummaryViewMode: this.isSummaryViewMode,
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            value: this.context.genericObject.getComputed(this.detailViewItem.name, {
                read: (observable) => {
                    if (this.detailViewItem.dotNetTypeName != null && TypeConverter.typeIsArray(this.detailViewItem.dotNetTypeName)) {
                        const value = observable();

                        if (value != null) {
                            return value.join(',');
                        } else {
                            return value;
                        }
                    } else {
                        return observable();
                    }
                },
                write: (observable, value) => {
                    if (this.detailViewItem.dotNetTypeName != null && TypeConverter.typeIsArray(this.detailViewItem.dotNetTypeName)) {
                        const array = value.split(',');
                        observable(array);
                    } else {
                        if(value === '')
                            observable(null);
                        else
                            observable(value);
                    }
                }
            }),
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            label: new this.LabelViewModel({
                isVisible: !!this.detailViewItem.unitErgName,
                ergName: this.detailViewItem.unitErgName
            }),
            onSubmit: this.params.onSubmit,
            maxLength: this.getLength()
        };

        return parameter;
    }

    public static parseChar(value: any): boolean {
        return value != null && value.toString().length === 1 && !(value instanceof Array);
    }

    public static isNaturalNumber(value: string, func: (value: string) => boolean): boolean {
        value += '';
        return value.indexOf('.') === -1 && !isNaN(+value) && func(value);
    }

    public static isWholeNumber(value: string, func: (value: string) => boolean): boolean {
        return !isNaN(+value) && func(value);
    }

    public static parseArrayOfType(value: Array<string>, func: (value: string) => boolean): boolean {
        let result: boolean = true;

        if (func != null) {
            for (let i = 0; i < value.length; i++) {
                result = result && func(value[i]);
            }
        }

        return result;
    }
}

ComponentManager.setFactory('TextBox', TextBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');